import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "utilities/i18n";
import { useHistory, useQueryParamString } from "polyrouter";
import { useLocation } from "react-router";
import { DISMISS, useNotification } from "collections/Notifications";
import { FloatingInput, LoadingIndicator, SubmitButton } from "elements";
import { SelectedTenant } from "polyrouter";
import {
  useCreateTenantMutation,
  User,
  useTenantQuery,
  useUpdateUserMutation,
  useUserByIdQuery
} from "generated/graphql";
import { generatePath } from "react-router";
import { hasBackgroundImage } from "utilities/misc";
import logo from "assets/images/poly_logo_lens.svg";
import "./CompleteProfile.scss";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import { State } from "store";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

export type UserProps =
  | Pick<User, "user_id" | "given_name" | "family_name">
  | undefined
  | null;

export const CompleteProfile: React.FC = () => {
  const theme = useSelector((state: State) => state.userMeta.theme);
  const { pathname } = useLocation();
  const lowerCaseTheme = theme.toLowerCase();
  const backgroundStyle = hasBackgroundImage(pathname, lowerCaseTheme);

  return (
    <div
      className={`main theme-${lowerCaseTheme} complete-profile`}
      style={backgroundStyle}
      data-testid="complete-profile-container"
    >
      <div className="login-panel text-center pv-2 ph-2">
        <img className="logo pb-15" src={logo} alt="Poly | Lens" />
        <CompleteProfileLogic />
      </div>
    </div>
  );
};

const CompleteProfileLogic: React.FC = () => {
  const { user } = useAuth0();

  const [acceptingInvite] = useQueryParamString<string>("accept-invite");
  const [id] = useQueryParamString<string>("tid");
  const { data, fetching } = useUserByIdQuery({
    variables: {
      id: user?.sub ?? ""
    }
  });

  if (fetching) {
    return <LoadingIndicator className="theme-independent" />;
  } else if (id && acceptingInvite) {
    return <TenantName user={data?.user?.user} id={id} />;
  } else {
    return (
      <CompleteProfileData
        user={data?.user?.user}
        tenant={null}
        acceptingInvite={false}
        id={null}
      />
    );
  }
};

const TenantName: React.FC<{ user: UserProps; id: string }> = ({
  user,
  id
}) => {
  const { data, fetching } = useTenantQuery({
    variables: {
      id: id
    }
  });
  const tenantName = data?.tenant?.name;

  if (fetching) {
    return <LoadingIndicator />;
  } else {
    return (
      <CompleteProfileData
        user={user}
        tenant={tenantName}
        acceptingInvite={true}
        id={id}
      />
    );
  }
};

const CompleteProfileData: React.FC<{
  user: UserProps;
  tenant: string | null | undefined;
  acceptingInvite: boolean;
  id: string | null | undefined;
}> = ({ user, tenant: initialTenant, acceptingInvite, id }) => {
  const { t } = useTranslation();
  const [createTenantResult, createTenant] = useCreateTenantMutation();
  const [updateUserResult, updateUser] = useUpdateUserMutation();
  const [thisTenantId, setThisTenantId] = useQueryParamString<SelectedTenant>(
    "tenantId"
  );

  const anyFetching = createTenantResult.fetching || updateUserResult.fetching;
  const initialState = {
    first_name: user?.given_name || "",
    last_name: user?.family_name || "",
    account_name: initialTenant || ""
  };

  const [profileState, setState] = useState(initialState);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, id } = e.target;
    setState({ ...profileState, [id]: value });
  };

  const [addNotification] = useNotification();

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let userSuccess = true;
    let tenantSuccess = true;
    if (!e.currentTarget.checkValidity()) {
      Array.from(e.currentTarget.elements).forEach(element => {
        element.dispatchEvent(new Event("focus"));
        element.dispatchEvent(new Event("blur"));
      });

      return;
    }

    if (!initialState.first_name || !initialState.last_name) {
      userSuccess = false;
      const userResult = await updateUser({
        id: user?.user_id || "",
        data: {
          given_name: profileState.first_name,
          family_name: profileState.last_name,
          name: `${profileState.first_name} ${profileState.last_name}`
        }
      });

      if (userResult.data?.updateUser?.user) {
        userSuccess = true;
      }
    }

    if (!acceptingInvite) {
      tenantSuccess = false;

      const tenantResult = await createTenant({
        input: {
          name: profileState.account_name
        }
      });

      if (!tenantResult.error) {
        tenantSuccess = true;
        const tenantId = tenantResult?.data?.createTenant?.tenant?.id || "";
        setThisTenantId(tenantId);

        addNotification({
          type: "success",
          message: t("ACCOUNT_ADD_SUCCESS", {
            name: tenantResult.data?.createTenant?.tenant.name || t("UNKNOWN")
          }),
          dismiss: DISMISS.DEFAULT,
          persistThroughNav: true
        });
      }
    }

    if (!userSuccess || !tenantSuccess) {
      addNotification({
        type: "error",
        message: t("TRY_AGAIN"),
        dismiss: DISMISS.USER_ONLY
      });
    }
  };

  if (thisTenantId) {
    return <Redirect />;
  }

  return (
    <form className="form text-left" onSubmit={submit} noValidate>
      <h2 className="text-center text-default pb-2">{t("COMPLETE_PROFILE")}</h2>
      <div>
        <FloatingInput
          id="first_name"
          name="First Name"
          label={t("FIRST_NAME")}
          value={profileState.first_name}
          readOnly={initialState.first_name !== ""}
          maxLength={50}
          required
          onChange={handleProfileChange}
        />
      </div>
      <div>
        <FloatingInput
          id="last_name"
          name="Last Name"
          label={t("LAST_NAME")}
          value={profileState.last_name}
          readOnly={initialState.last_name !== ""}
          maxLength={50}
          required
          onChange={handleProfileChange}
        />
      </div>
      <div>
        <FloatingInput
          classes={["account-info"]}
          id="account_name"
          name="Account Name"
          label={t("ACCOUNT_NAME")}
          value={profileState.account_name}
          readOnly={initialState.account_name !== ""}
          maxLength={50}
          required
          onChange={handleProfileChange}
        />
      </div>
      {!acceptingInvite && (
        <ul className="mb-0">
          <li className="text-sm">
            {t("NAME_THIS_ACCOUNT_INSTRUCTIONS")}
            <br />
            {t("NAME_THIS_ACCOUNT_INSTRUCTIONS_2")}
          </li>
        </ul>
      )}
      {anyFetching && <LoadingIndicator />}
      <div className="text-center pt-3">
        <SubmitButton
          isSubmitting={updateUserResult.fetching}
          disabled={anyFetching}
          labelKey="CONTINUE"
          submittingLabelKey="SAVING"
        />
      </div>
    </form>
  );
};

const Redirect: React.FC = () => {
  const {
    tenant,
    refetch,
    tenants: { tenants }
  } = useContext(SelectedTenantContext);
  const history = useHistory();
  refetch();

  useEffect(() => {
    if (tenant && (tenants?.tenants || []).length > 0) {
      const path = generatePath(`/home`, { tenantId: tenant.id });
      history.push(path);
    }
  }, [tenant, tenants, history]);

  return <LoadingIndicator />;
};

export default CompleteProfile;
